import {
  AudienceExternalIdentityVerificationTargetingAttributeFragment,
  AudienceExternalIdentityVerificationTargetingAttributeInput,
  External_Identity_Verification_Operator,
  ExternalIdentityVerificationProvider,
} from '@occc/graphql';
import { ExternalIdentityVerificationFormValue } from '../../types';

const toHandleObjects = (handles: string[]) =>
  handles.map((handle) => ({ handle: handle, name: handle }));

const getAttributesByOperator = (
  items: AudienceExternalIdentityVerificationTargetingAttributeFragment[],
  operator: External_Identity_Verification_Operator,
) =>
  items.find((item) => item.identityVerificationOperator === operator)?.verificationAttributes ??
  [];

export const getExternalIdentityVerificationInitialValues = (
  items: AudienceExternalIdentityVerificationTargetingAttributeFragment[] = [],
): ExternalIdentityVerificationFormValue | undefined => {
  const inAll = getAttributesByOperator(items, External_Identity_Verification_Operator.InAll);
  const inAny = getAttributesByOperator(items, External_Identity_Verification_Operator.InAny);
  const notInAll = getAttributesByOperator(items, External_Identity_Verification_Operator.NotInAll);

  const allEmpty = inAll.length === 0 && inAny.length === 0 && notInAll.length === 0;
  if (allEmpty && items.length === 0) return undefined;

  return {
    inAll: { verificationAttributes: toHandleObjects(inAll) },
    inAny: { verificationAttributes: toHandleObjects(inAny) },
    notInAll: { verificationAttributes: toHandleObjects(notInAll) },
  };
};

const extractHandles = (items: { handle: string }[]) => items.map((item) => item.handle);

const isEmpty = (value?: { verificationAttributes: { handle: string }[] }) =>
  !value || !value.verificationAttributes || value.verificationAttributes.length === 0;

export const mapExternalIdentityVerificationToArray = (
  value?: ExternalIdentityVerificationFormValue | null,
): AudienceExternalIdentityVerificationTargetingAttributeInput[] => {
  if (!value || (isEmpty(value.inAll) && isEmpty(value.inAny) && isEmpty(value.notInAll))) {
    return [];
  }

  const externalIdentityVerificationTargets: AudienceExternalIdentityVerificationTargetingAttributeInput[] =
    [];

  if (!isEmpty(value.inAll)) {
    externalIdentityVerificationTargets.push({
      operator: External_Identity_Verification_Operator.InAll,
      provider: ExternalIdentityVerificationProvider.Idme,
      verificationAttributes: extractHandles(value.inAll.verificationAttributes),
    });
  }
  if (!isEmpty(value.inAny)) {
    externalIdentityVerificationTargets.push({
      operator: External_Identity_Verification_Operator.InAny,
      provider: ExternalIdentityVerificationProvider.Idme,
      verificationAttributes: extractHandles(value.inAny.verificationAttributes),
    });
  }
  if (!isEmpty(value.notInAll)) {
    externalIdentityVerificationTargets.push({
      operator: External_Identity_Verification_Operator.NotInAll,
      provider: ExternalIdentityVerificationProvider.Idme,
      verificationAttributes: extractHandles(value.notInAll.verificationAttributes),
    });
  }
  return externalIdentityVerificationTargets;
};

export const externalIdentityVerificationProviderToDomain: Record<
  ExternalIdentityVerificationProvider,
  string
> = {
  [ExternalIdentityVerificationProvider.Idme]: 'id.me',
};

export function getDomainForProvider(
  provider?: ExternalIdentityVerificationProvider,
): string | null {
  if (!provider) return null;
  return externalIdentityVerificationProviderToDomain[provider];
}
